/**
 * useTableSubscription — primitive real-time subscription hook.
 *
 * Subscribes to Postgres change events on one or more tables via Supabase
 * Realtime. When any subscribed table fires an INSERT, UPDATE, or DELETE event
 * the provided React Query keys are invalidated so all dependent components
 * automatically refetch fresh data.
 *
 * Prerequisites (Supabase dashboard):
 *   1. Enable Realtime for each table in Table Editor → "Realtime" toggle.
 *   2. For UPDATE/DELETE events that need old-row data, set REPLICA IDENTITY
 *      FULL on the table. For cache invalidation (this hook's purpose),
 *      this is not required — any event is sufficient to trigger a refresh.
 *
 * Usage:
 *   useTableSubscription("my-channel", [
 *     { table: "stock_movements", invalidateKeys: [["stock-movements"], ["stock-levels"]] },
 *     { table: "goods_receipts",  invalidateKeys: [["goods-receipts"]] },
 *   ]);
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface TableSubscriptionEntry {
  /** Postgres table name (public schema). */
  table: string;
  /** React Query key arrays to invalidate on any change to this table. */
  invalidateKeys: string[][];
}

/**
 * @param channelName  Unique Supabase Realtime channel name. Use a stable
 *   string per hook call site — duplicates will silently subscribe twice.
 * @param tables  Tables to watch and the query keys to invalidate.
 */
export function useTableSubscription(
  channelName: string,
  tables: TableSubscriptionEntry[]
): void {
  const qc = useQueryClient();
  const { user } = useAuth();

  // Stable refs so the effect closure always has the latest values without
  // re-subscribing every render.
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const tablesRef = useRef(tables);
  tablesRef.current = tables;

  useEffect(() => {
    // Do not subscribe when unauthenticated (RLS would reject events anyway).
    if (!user) return;

    const channel = supabase.channel(channelName);

    for (const entry of tablesRef.current) {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table: entry.table },
        () => {
          for (const key of entry.invalidateKeys) {
            void qcRef.current.invalidateQueries({ queryKey: key });
          }
        }
      );
    }

    channel.subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  // channelName is intentionally excluded from deps — it should be stable.
  // Re-subscribing on user change (login/logout) is the only dynamic dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
